import { useContext, useEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import { ISalesforceNavigation, LoadingButton, SnackBarContext } from '@frontend-workspace/react-shared-lib';
import { useTranslation } from 'react-i18next';
import { makeRequest } from '../requestUtils';
import { salesforceContractAssetsUrl, salesforceRecordUrl } from '../sales-force-account-reconciliation/reconciliation-links';

interface SalesforceRecordLinkProps {
  baseUrl: string;
  recordType: 'Account' | 'Contract';
  companyNumber: string;
  contractNumber?: string;
  destination?: 'record' | 'contractAssets';
}

export function SalesforceRecordLink({ baseUrl, recordType, companyNumber, contractNumber, destination = 'record' }: SalesforceRecordLinkProps) {
  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const pendingTab = useRef<Window | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const canResolve = Boolean(baseUrl && companyNumber.trim() &&
    (recordType === 'Account' || contractNumber?.trim()));
  const action = t(`salesforceNavigation.${destination === 'contractAssets' ? 'openContractAssets' : `open${recordType}`}`);
  const title = error ?? (loading ? t('salesforceNavigation.loading') :
    canResolve ? action : t('salesforceNavigation.NotLinked'));
  const label = destination === 'contractAssets' ? t('salesforceNavigation.assetsLabel') : 'Salesforce';

  useEffect(() => {
    setLoading(false);
    setError(undefined);
    return () => {
      pendingTab.current?.close();
      pendingTab.current = null;
    };
  }, [baseUrl, companyNumber, contractNumber, recordType, destination]);

  const reportFailure = (status: string) => {
    const content = t(`salesforceNavigation.${status}`);
    setError(content);
    setSnackbarMessage({ content, type: 'error' });
  };

  const open = async () => {
    if (pendingTab.current) return;
    const tab = window.open('about:blank', '_blank');
    if (!tab) {
      reportFailure('popupBlocked');
      return;
    }
    pendingTab.current = tab;
    setLoading(true);
    setError(undefined);
    try {
      // Open synchronously to avoid popup blocking; detach before awaiting the lookup.
      tab.opener = null;
      const referrer = tab.document.createElement('meta');
      referrer.name = 'referrer';
      referrer.content = 'no-referrer';
      tab.document.head.appendChild(referrer);
      tab.document.title = t('salesforceNavigation.loading');
      tab.document.body.textContent = t('salesforceNavigation.loading');
      const ownerPath = `companies/${encodeURIComponent(companyNumber)}`;
      const path = recordType === 'Contract'
        ? `${ownerPath}/contracts/${encodeURIComponent(contractNumber ?? '')}` : ownerPath;
      const navigation: ISalesforceNavigation | undefined = await makeRequest(`${path}/salesforce-navigation`);
      if (pendingTab.current !== tab || tab.closed) return;
      if (navigation?.status === 'Linked' && navigation.recordId?.trim()) {
        tab.location.replace(destination === 'contractAssets'
          ? salesforceContractAssetsUrl(navigation.recordId, baseUrl)
          : salesforceRecordUrl(recordType, navigation.recordId, baseUrl));
      } else {
        tab.close();
        reportFailure(navigation?.status === 'NotLinked' || navigation?.status === 'Ambiguous'
          ? navigation.status : 'Unavailable');
      }
    } catch {
      if (pendingTab.current === tab && !tab.closed) {
        tab.close();
        reportFailure('Unavailable');
      }
    } finally {
      if (pendingTab.current === tab) {
        pendingTab.current = null;
        setLoading(false);
      }
    }
  };

  return <Tooltip title={title}>
    <span className="text-nowrap" onClick={event => event.stopPropagation()}>
      <LoadingButton size="small" variant="text" color="secondary" onClick={open}
        loading={loading} disabled={!canResolve} aria-label={action} aria-busy={loading}>
        {label} <span aria-hidden="true">↗</span>
      </LoadingButton>
    </span>
  </Tooltip>;
}

export function CompanySalesforceLink({ companyNumber, baseUrl }: { companyNumber: string; baseUrl: string }) {
  return <SalesforceRecordLink baseUrl={baseUrl} recordType="Account" companyNumber={companyNumber} />;
}
